import { useSnackbar } from '../../contexts/SnackbarContext'
import UpsertDocumentBucket from '../../queries/UpsertDocumentBucket'
import Documents from '../../queries/DocumentsQuery'
import { useMutation } from '@apollo/react-hooks'
import { DocumentCategory, DocumentPhase } from '../constants'
import { getIsOperatingCompany, getIsFinancialCategory } from './utils'
import { createCustomEntityId, parseCustomEntityId } from '../utils'
import {
  generateNewCacheDataAddingDocument,
  generateNewCacheDataExludingDocument,
} from '../utils/apollo-cache-manipulation'

function useUpsertDocument({ onClose, editingAttrs, loanId }) {
  const { setSuccessMessage, setErrorMessage } = useSnackbar()

  const [upsertDocumentBucketMutation, { loading }] = useMutation(
    UpsertDocumentBucket,
    {
      onCompleted: () => {
        setSuccessMessage(`Document ${editingAttrs ? 'updated' : 'created'}`)
        onClose()
      },
      onError: () =>
        setErrorMessage(
          `There was an error ${
            editingAttrs ? 'updating' : 'creating'
          } the document`
        ),
    }
  )

  const onSubmit = (formData) =>
    upsertDocumentBucketMutation({
      variables: {
        input: parseFormData({ formData, editingAttrs }),
      },
      update: (cache, mutationResult) => {
        const newDocumentOwner = formData.entity
        const previousDocumentOwner = editingAttrs
          ? createCustomEntityId(editingAttrs.owner)
          : null

        if (newDocumentOwner === previousDocumentOwner) return

        const cacheData = cache.readQuery({
          query: Documents,
          variables: { rid: loanId },
        })

        const upsertedDocument =
          mutationResult.data.upsertDocumentBucket.document

        let newCacheData = cacheData

        if (previousDocumentOwner) {
          newCacheData = generateNewCacheDataExludingDocument({
            cacheData: newCacheData,
            documentId: upsertedDocument.rid,
          })
        }

        newCacheData = generateNewCacheDataAddingDocument({
          cacheData: newCacheData,
          newDocument: upsertedDocument,
          owner: newDocumentOwner,
        })

        cache.writeQuery({
          query: Documents,
          variables: { rid: loanId },
          data: newCacheData,
        })
      },
    })

  return { onSubmit, loading }
}

function parseFormData({ formData, editingAttrs }) {
  const { entity: customEntityId, category } = formData
  const { entityType, entityId } = parseCustomEntityId(customEntityId)
  const isOperatingCompany = getIsOperatingCompany(customEntityId)
  const isNotFinancialCategory = !getIsFinancialCategory(category)

  return {
    documentId: editingAttrs?.document.rid || null,
    documentFields: {
      entityType,
      entityId,
      name: formData.name,
      adminOnly: formData.adminOnly === 'true',
      esignable: formData.esignable === 'true',
      description: formData.description?.substr(0, 255) || null,
      phase:
        isOperatingCompany && isNotFinancialCategory
          ? DocumentPhase.main
          : formData.phase,
      category: isOperatingCompany
        ? formData.category
        : DocumentCategory.guarantor_default,
    },
  }
}

export default useUpsertDocument
